import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { TESTIMONIALS } from "../../lib/data";
import { Star, ExternalLink, CheckCircle2 } from "lucide-react";
import { fetchReviews } from "../../lib/api";

const FALLBACK = TESTIMONIALS.map((t) => ({
  author_name: t.name,
  author_photo: null,
  rating: 5,
  text: t.quote,
  relative_time: t.location,
  tag: t.tag,
  source: "practice",
}));

const MAX_CARDS = 6;

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: (i % 3) * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Testimonials = () => {
  const [live, setLive] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    fetchReviews()
      .then((data) => {
        if (!mounted) return;
        if (data?.available && Array.isArray(data.reviews) && data.reviews.length) {
          setLive({
            rating: data.rating,
            total: data.total_ratings,
            placeId: data.place_id,
            reviews: data.reviews.map((r) => ({
              author_name: r.author_name,
              author_photo: r.author_photo,
              rating: r.rating || 5,
              text: r.text || "",
              relative_time: r.relative_time,
              tag: "Verified Google Review",
              source: "google",
            })),
          });
        }
      })
      .catch(() => {})
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  const items = [...(live?.reviews || []), ...FALLBACK].slice(0, MAX_CARDS);
  const isLive = !!live;
  const avg = live?.rating ?? 4.9;
  const total = live?.total ?? 10000;
  const totalLabel = isLive
    ? `${total.toLocaleString()} Google reviews`
    : "Trusted by 10,000+ patients";
  const reviewsUrl = live?.placeId
    ? `https://search.google.com/local/reviews?placeid=${live.placeId}`
    : null;

  return (
    <section id="testimonials" className="py-20 sm:py-28 px-6 sm:px-10 lg:px-14 bg-paper">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-14 max-w-3xl mx-auto"
        >
          <p className="eyebrow mb-3">Patient Stories</p>
          <h2 className="font-sans font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-ink leading-tight">
            What Our Patients Say
          </h2>

          <div className="mt-6 inline-flex items-center gap-2 bg-bone-2 border border-line rounded-full px-5 py-2.5">
            <div className="flex gap-0.5">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  size={15}
                  className={i < Math.round(avg) ? "fill-terra text-terra" : "text-line"}
                />
              ))}
            </div>
            <span className="text-sm font-semibold text-ink font-mono" data-testid="reviews-avg">
              {Number(avg).toFixed(1)}
            </span>
            <span className="text-sm text-ink-soft" data-testid="reviews-count">
              · {totalLabel}
            </span>
            {isLive && (
              <span
                className="text-[10px] font-semibold uppercase tracking-wider text-forest bg-paper border border-line rounded-full px-2 py-0.5 font-mono"
                data-testid="reviews-live-badge"
              >
                Live
              </span>
            )}
          </div>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((t, i) => (
            <motion.article
              key={`${t.author_name}-${i}`}
              data-testid={`testimonial-${i}`}
              custom={i}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-40px" }}
              variants={fadeUp}
              className="card-hover bg-bone-2 border border-line rounded-2xl p-7 flex flex-col"
            >
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold text-forest tracking-wide">
                  {t.tag}
                </p>
                {t.source === "google" && (
                  <span
                    title="Verified Google Review"
                    data-testid={`testimonial-${i}-google-badge`}
                    className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-forest bg-paper border border-line rounded-full px-2 py-0.5 font-mono"
                  >
                    <CheckCircle2 size={11} strokeWidth={2.4} /> Google
                  </span>
                )}
              </div>
              <div className="flex gap-0.5 mb-4">
                {[...Array(5)].map((_, idx) => (
                  <Star
                    key={idx}
                    size={16}
                    className={
                      idx < (t.rating || 5) ? "fill-terra text-terra" : "text-line"
                    }
                  />
                ))}
              </div>
              <p className="text-[15px] text-ink leading-relaxed flex-1 italic line-clamp-7">
                "{t.text}"
              </p>
              <div className="mt-6 pt-5 border-t border-line flex items-center gap-3">
                {t.author_photo ? (
                  <img
                    src={t.author_photo}
                    alt={t.author_name}
                    className="w-10 h-10 rounded-full object-cover border border-line"
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-forest text-white flex items-center justify-center font-bold">
                    {t.author_name.charAt(0)}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-sans font-semibold text-sm text-ink truncate">
                    {t.author_name}
                  </p>
                  <p className="text-xs text-ink-soft truncate">{t.relative_time}</p>
                </div>
              </div>
            </motion.article>
          ))}
        </div>

        {isLive && reviewsUrl && (
          <div className="text-center mt-12">
            <a
              href={reviewsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-paper border border-line hover:border-forest hover:text-forest text-ink px-6 py-3 rounded-full text-sm font-semibold transition"
              data-testid="reviews-view-all"
            >
              View all {total.toLocaleString()} reviews on Google <ExternalLink size={14} />
            </a>
          </div>
        )}

        {loading && !isLive && (
          <p className="text-center mt-8 text-xs text-ink-soft/60">
            Loading latest Google reviews…
          </p>
        )}
      </div>
    </section>
  );
};
